// The caption style panel (video-caption font/size/color/placement controls)
// and the Settings page (profile/password/danger-zone). Merged into
// `translations` by ../i18n.js.
export const captionSettings = {
  en: {
    captionStyle: {
      videoLanguages: "Video languages",
      originalLanguage: "Original language",
      chooseLanguagesTitle: "Choose and prioritise video caption languages",
      chooseLanguagesDisabledTitle: "Create a translation to enable this option",
      captionOrderTitle: "Caption order",
      captionOrderHint: "The first selected language appears on top.",
      loading: "Loading…",
      moveUp: (label) => `Move ${label} up`,
      moveDown: (label) => `Move ${label} down`,
      defaultFont: "Default",
      presets: "Presets",
      hex: "Hex",
      textColor: "Text color",
      outlineColor: "Outline color",
      backgroundColorLabel: "Background color",
      bold: "Bold",
      italic: "Italic",
      decreaseSize: "Decrease caption size",
      increaseSize: "Increase caption size",
      alignLeft: "Align left",
      alignCenter: "Align center",
      alignRight: "Align right",
      placement: "Placement",
      top: "Top",
      bottom: "Bottom",
      verticalPlacement: "Caption vertical placement",
      background: "Background",
      backgroundBox: "Background: box",
      backgroundOutline: "Background: outline",
      backgroundNone: "Background: none",
      backgroundOpacity: "Background opacity",
      colorNames: {
        White: "White",
        Yellow: "Yellow",
        Sky: "Sky",
        Black: "Black",
      },
    },
    settings: {
      pageTitle: "Account settings",
      pageSubtitle: "Manage your profile and account security.",
      profileTitle: "Profile",
      profileDescription: "Update the name shown across your Arkii workspace.",
      fullNameLabel: "Full name",
      fullNamePlaceholder: "Your full name",
      saving: "Saving...",
      saveName: "Save name",
      nameUpdated: "Name updated.",
      passwordTitle: "Password",
      passwordDescription: "Use a strong password that you do not reuse on other services.",
      currentPasswordLabel: "Current password",
      newPasswordLabel: "New password",
      newPasswordHint: "8+ characters",
      confirmPasswordLabel: "Confirm new password",
      passwordsMismatch: "The new passwords do not match.",
      passwordRequirementsNote: "Passwords must include uppercase and lowercase letters, a number, and a symbol.",
      changing: "Changing...",
      changePassword: "Change password",
      passwordChanged: "Password changed. Please log in again.",
      dangerZoneTitle: "Danger zone",
      dangerZoneDescription: "Deleting your account permanently removes your profile and all associated data.",
      deleteAccountTitle: "Delete this account",
      deleteAccountNote: "This action cannot be undone.",
      deleteAccountButton: "Delete account",
      deleteDialogTitle: "Confirm permanent account deletion",
      deleteDialogDescription:
        "This permanently removes your account, projects, transcriptions, and remaining credits. There is no recovery period.",
      confirmEmailLabel: "Type your email to confirm",
      deleteAcknowledgement: "I understand that all account data will be permanently deleted.",
      cancel: "Cancel",
      deleting: "Deleting...",
      permanentlyDeleteAccount: "Permanently delete account",
      accountDeleted: "Account deleted.",
      showPassword: "Show password",
      hidePassword: "Hide password",
    },
  },
  ar: {
    captionStyle: {
      videoLanguages: "لغات الفيديو",
      originalLanguage: "اللغة الأصلية",
      chooseLanguagesTitle: "اختيار لغات ترجمة الفيديو وترتيب أولويتها",
      chooseLanguagesDisabledTitle: "أنشئ ترجمة لتفعيل هذا الخيار",
      captionOrderTitle: "ترتيب لغات الترجمة",
      captionOrderHint: "تظهر أول لغة مختارة في الأعلى.",
      loading: "جارٍ التحميل…",
      moveUp: (label) => `نقل ${label} للأعلى`,
      moveDown: (label) => `نقل ${label} للأسفل`,
      defaultFont: "الافتراضي",
      presets: "ألوان جاهزة",
      hex: "Hex",
      textColor: "لون النص",
      outlineColor: "لون الحدود الخارجية",
      backgroundColorLabel: "لون الخلفية",
      bold: "عريض",
      italic: "مائل",
      decreaseSize: "تصغير حجم الترجمة",
      increaseSize: "تكبير حجم الترجمة",
      alignLeft: "محاذاة لليسار",
      alignCenter: "محاذاة للوسط",
      alignRight: "محاذاة لليمين",
      placement: "الموضع",
      top: "أعلى",
      bottom: "أسفل",
      verticalPlacement: "الموضع الرأسي للترجمة",
      background: "الخلفية",
      backgroundBox: "الخلفية: صندوق",
      backgroundOutline: "الخلفية: حدود خارجية",
      backgroundNone: "الخلفية: بدون",
      backgroundOpacity: "شفافية الخلفية",
      colorNames: {
        White: "أبيض",
        Yellow: "أصفر",
        Sky: "سماوي",
        Black: "أسود",
      },
    },
    settings: {
      pageTitle: "إعدادات الحساب",
      pageSubtitle: "إدارة ملفك الشخصي وأمان حسابك.",
      profileTitle: "الملف الشخصي",
      profileDescription: "تحديث الاسم الظاهر في مساحة عمل آركـي الخاصة بك.",
      fullNameLabel: "الاسم الكامل",
      fullNamePlaceholder: "اسمك الكامل",
      saving: "جارٍ الحفظ...",
      saveName: "حفظ الاسم",
      nameUpdated: "تم تحديث الاسم.",
      passwordTitle: "كلمة المرور",
      passwordDescription: "استخدم كلمة مرور قوية لا تستخدمها في خدمات أخرى.",
      currentPasswordLabel: "كلمة المرور الحالية",
      newPasswordLabel: "كلمة المرور الجديدة",
      newPasswordHint: "8 أحرف فأكثر",
      confirmPasswordLabel: "تأكيد كلمة المرور الجديدة",
      passwordsMismatch: "كلمتا المرور الجديدتان غير متطابقتين.",
      passwordRequirementsNote: "يجب أن تحتوي كلمة المرور على حرف كبير وحرف صغير ورقم ورمز.",
      changing: "جارٍ التغيير...",
      changePassword: "تغيير كلمة المرور",
      passwordChanged: "تم تغيير كلمة المرور. يرجى تسجيل الدخول مجددًا.",
      dangerZoneTitle: "منطقة الخطر",
      dangerZoneDescription: "حذف حسابك يزيل نهائيًا ملفك الشخصي وجميع البيانات المرتبطة به.",
      deleteAccountTitle: "حذف هذا الحساب",
      deleteAccountNote: "لا يمكن التراجع عن هذا الإجراء.",
      deleteAccountButton: "حذف الحساب",
      deleteDialogTitle: "تأكيد حذف الحساب نهائيًا",
      deleteDialogDescription:
        "سيؤدي هذا إلى إزالة حسابك ومشاريعك وتفريغاتك الصوتية والرصيد المتبقي نهائيًا. لا توجد فترة استرجاع.",
      confirmEmailLabel: "اكتب بريدك الإلكتروني للتأكيد",
      deleteAcknowledgement: "أدرك أن جميع بيانات الحساب ستُحذف نهائيًا.",
      cancel: "إلغاء",
      deleting: "جارٍ الحذف...",
      permanentlyDeleteAccount: "حذف الحساب نهائيًا",
      accountDeleted: "تم حذف الحساب.",
      showPassword: "إظهار كلمة المرور",
      hidePassword: "إخفاء كلمة المرور",
    },
  },
};
